/**
 * Detect whether an email address shares its domain with the current
 * org's primary domain — i.e. "is this a coworker, or an external
 * prospect?"
 *
 * Used to dispatch the chip / slideout-header CTA between "Add to CRM"
 * (different domain → prospect) and "Add as user" (same domain →
 * coworker, invite to org).
 *
 * Comparison rule mirrors the backend
 * `should_create_contact_for_email` helper (apps/email/services/email_utils.py):
 *
 *   - Both inputs lowercased + trimmed (mixed-case domains don't bypass).
 *   - Freemail domains (gmail.com, etc.) are NEVER treated as the org's
 *     domain. Otherwise, every personal-email user would falsely see
 *     "Add as user" on every personal-email chip.
 *   - Without a usable org-anchor email, returns `false` (conservative:
 *     fall through to "Add to CRM").
 *
 * The set of freemail domains intentionally tracks the backend's
 * `freemail` Python library at a high level. We hard-code the common
 * ones here rather than introducing a frontend dependency on a domain-
 * list; the worst-case mistake (an obscure freemail provider treated
 * as "an org domain") is recoverable — the user just sees "Add as user"
 * on their personal-email chip and can cancel from the confirmation
 * modal before sending anything externally.
 */

const FREEMAIL_DOMAINS: ReadonlySet<string> = new Set([
  'gmail.com',
  'yahoo.com',
  'yahoo.co.uk',
  'yahoo.co.in',
  'outlook.com',
  'hotmail.com',
  'live.com',
  'msn.com',
  'icloud.com',
  'me.com',
  'mac.com',
  'aol.com',
  'protonmail.com',
  'proton.me',
  'gmx.com',
  'gmx.de',
  'mail.com',
  'yandex.com',
  'yandex.ru',
  'zoho.com',
  'fastmail.com',
  'tutanota.com',
  'hey.com',
  'duck.com',
]);

/**
 * Returns the domain portion of an email address (lowercased), or `null`
 * when the input doesn't contain an `@`. Whitespace is stripped from
 * the whole address before splitting.
 */
export function domainOfEmail(email: string | null | undefined): string | null {
  if (!email) {
    return null;
  }
  const normalized = email.trim().toLowerCase();
  const at = normalized.lastIndexOf('@');
  // Reject malformed values: missing `@`, empty local-part (e.g. `"@acme.com"`),
  // or empty domain (e.g. `"alice@"`). Without the empty-local-part guard,
  // `"@acme.com"` would falsely register as same-domain as `"bob@acme.com"`.
  if (at <= 0 || at === normalized.length - 1) {
    return null;
  }
  return normalized.slice(at + 1);
}

/**
 * Whether the given domain is a freemail provider (gmail, yahoo, etc.).
 * Used to suppress same-domain detection when the org's anchor email
 * is a personal address.
 */
export function isFreemailDomain(domain: string | null | undefined): boolean {
  if (!domain) {
    return false;
  }
  return FREEMAIL_DOMAINS.has(domain.toLowerCase());
}

/**
 * True when `email` shares its domain with `anchorEmail` (the current
 * user's email, or any other "this is my org's primary address"
 * reference). Returns `false` when the anchor is freemail, when either
 * input is missing/malformed, or when the domains differ.
 *
 * @param email Candidate email (typically from a chip's `ahoy_email`).
 * @param anchorEmail Anchor email defining "the org's domain" — usually
 *   the logged-in user's email. Pass `currentUser.email`.
 */
export function isSameDomainAsOrg(
  email: string | null | undefined,
  anchorEmail: string | null | undefined
): boolean {
  const emailDomain = domainOfEmail(email);
  const anchorDomain = domainOfEmail(anchorEmail);
  if (!emailDomain || !anchorDomain) {
    return false;
  }
  if (isFreemailDomain(anchorDomain)) {
    // Can't infer "the org's domain" from a personal address. Bail
    // safely so the surface falls back to "Add to CRM".
    return false;
  }
  return emailDomain === anchorDomain;
}
